import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Auth pages
import Login from "./pages/auth/Login";
import SignupStep1 from "./pages/auth/SignupStep1";
import SignupStep2OTP from "./pages/auth/SignupStep2OTP";
import SignupStep3Form from "./pages/auth/SignupStep3Form";
import ForgotPassword from "./pages/auth/ForgotPassword";

// Dashboard pages
import DashboardRouter from "./pages/dashboard/DashboardRouter";

// Bill pages
import BillCreation from './pages/bill/BillCreation';
import CreateBill from "./pages/bill/CreateBill";
import EditBill from "./pages/bill/EditBill";
import BillDetails from "./pages/bill/BillDetails";

// Inventory pages
import Inventory from "./pages/inventory/Inventory";
import CreateInventory from "./pages/inventory/CreateInventory";
import AddProduct from "./pages/inventory/AddProduct";
import AddRawMaterial from "./pages/inventory/AddRawMaterial";
import RawMaterialDetails from "./pages/inventory/RawMaterialDetails";
import ProductDetails from "./pages/inventory/ProductDetails";

// Products pages (for staff) - Not needed, using Inventory.jsx for both roles

// Staff pages
import Staff from "./pages/staff/Staff";
import ViewStaffDetails from "./pages/staff/ViewStaffDetails";
import AttendanceSalaryDetails from "./pages/staff/AttendanceSalaryDetails";
import MyAttendanceSalary from "./pages/staff/MyAttendanceSalary";
import MySalary from "./pages/staff/MySalary";
import MySalaryDetails from "./pages/staff/MySalaryDetails";
import MyTasks from "./pages/staff/MyTasks";
import MonthlyAttendanceCalendar from "./pages/staff/MonthlyAttendanceCalendar";
import StaffAttendanceAdmin from "./pages/staff/StaffAttendanceAdmin";
import StaffSalaryAdmin from "./pages/staff/StaffSalaryAdmin";
import StaffSalaryPayments from "./pages/staff/StaffSalaryPayments";
import StaffMonthlyPayments from "./pages/staff/StaffMonthlyPayments";

// Admin pages
import AdminStaffAttendanceSalary from "./pages/admin/AdminStaffAttendanceSalary";
import AdminStaffAttendanceCalendar from "./pages/admin/AdminStaffAttendanceCalendar";
import AdminStaffSalaryDetails from "./pages/admin/AdminStaffSalaryDetails";
import AdminTaskCalendar from "./pages/admin/AdminTaskCalendar";
import TaskDetailsPage from "./pages/admin/TaskDetailsPage";
import CustomerMeasurements from "./pages/admin/CustomerMeasurements";
import CustomerMeasurementForm from "./pages/admin/CustomerMeasurementForm";
import CustomerPaymentHistory from "./pages/admin/CustomerPaymentHistory";
import CustomerSchemePage from "./pages/admin/CustomerSchemePage";
import Suppliers from "./pages/admin/Suppliers";
import SupplierPaymentHistory from "./pages/admin/SupplierPaymentHistory";
import Import from "./pages/admin/Import";
import Export from "./pages/admin/Export";

// Profile page
import Profile from "./pages/profile/Profile";

// Expenses pages
import Expenses from "./pages/expenses/Expenses";
import AddExpense from "./pages/expenses/AddExpense";
import ExpenseDetails from "./pages/expenses/ExpenseDetails";
import ExpensePaymentHistory from "./pages/expenses/ExpensePaymentHistory";

// Schedule pages
import BoutiqueSchedule from "./pages/schedule/BoutiqueSchedule";
import ScheduleCalendar from "./pages/schedule/ScheduleCalendar";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import './App.css';

function App() {
  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <Routes>
      {/* Public auth routes - accessible only when NOT logged in */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupStep1 />
          </PublicRoute>
        }
      />

      <Route
        path="/verify-otp"
        element={
          <PublicRoute>
            <SignupStep2OTP />
          </PublicRoute>
        }
      />

      <Route
        path="/complete-signup"
        element={
          <PublicRoute>
            <SignupStep3Form />
          </PublicRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />

      {/* Protected routes - accessible only when logged in */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bills"
        element={
          <ProtectedRoute>
            <BillCreation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-bill"
        element={
          <ProtectedRoute>
            <CreateBill />
          </ProtectedRoute>
        }
      />
      <Route
        path="/edit-bill"
        element={
          <ProtectedRoute requiredRole="Admin">
            <EditBill />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bill-details"
        element={
          <ProtectedRoute>
            <BillDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/create"
        element={
          <ProtectedRoute>
            <CreateInventory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/products/new"
        element={
          <ProtectedRoute>
            <AddProduct />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/products/edit/:code"
        element={
          <ProtectedRoute>
            <AddProduct />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/raw-materials/new"
        element={
          <ProtectedRoute>
            <AddRawMaterial />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/raw-materials/edit/:id"
        element={
          <ProtectedRoute>
            <AddRawMaterial />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/raw-materials/:id"
        element={
          <ProtectedRoute>
            <RawMaterialDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/products/:code"
        element={
          <ProtectedRoute>
            <ProductDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff"
        element={
          <ProtectedRoute requiredRole="Admin">
            <Staff />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/attendance"
        element={
          <ProtectedRoute requiredRole="Admin">
            <StaffAttendanceAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/salary"
        element={
          <ProtectedRoute requiredRole="Admin">
            <StaffSalaryAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/:id"
        element={
          <ProtectedRoute requiredRole="Admin">
            <ViewStaffDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/:id/attendance-salary"
        element={
          <ProtectedRoute requiredRole="Admin">
            <AttendanceSalaryDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/:staffId/payments"
        element={
          <ProtectedRoute requiredRole="Admin">
            <StaffSalaryPayments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/:staffId/payments/:month/:year"
        element={
          <ProtectedRoute requiredRole="Admin">
            <StaffMonthlyPayments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/staff/:staffId/attendance-salary"
        element={
          <ProtectedRoute requiredRole="Admin">
            <AdminStaffAttendanceSalary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/staff/:staffId/attendance-calendar/:month/:year"
        element={
          <ProtectedRoute requiredRole="Admin">
            <AdminStaffAttendanceCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/staff/:staffId/salary-details/:month/:year"
        element={
          <ProtectedRoute requiredRole="Admin">
            <AdminStaffSalaryDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tasks"
        element={
          <ProtectedRoute requiredRole="Admin">
            <AdminTaskCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tasks/:date"
        element={
          <ProtectedRoute requiredRole="Admin">
            <TaskDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers"
        element={
          <ProtectedRoute requiredRole="Admin">
            <CustomerMeasurements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/suppliers"
        element={
          <ProtectedRoute requiredRole="Admin">
            <Suppliers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/suppliers/:supplierId/payments"
        element={
          <ProtectedRoute requiredRole="Admin">
            <SupplierPaymentHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import"
        element={
          <ProtectedRoute requiredRole="Admin">
            <Import />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/export"
        element={
          <ProtectedRoute requiredRole="Admin">
            <Export />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers/new"
        element={
          <ProtectedRoute requiredRole="Admin">
            <CustomerMeasurementForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers/edit/:tokenNo"
        element={
          <ProtectedRoute requiredRole="Admin">
            <CustomerMeasurementForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers/view/:tokenNo"
        element={
          <ProtectedRoute requiredRole="Admin">
            <CustomerMeasurementForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers/:tokenNo/payments"
        element={
          <ProtectedRoute requiredRole="Admin">
            <CustomerPaymentHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers/:customerTokenNo/schemes"
        element={
          <ProtectedRoute requiredRole="Admin">
            <CustomerSchemePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-attendance-salary"
        element={
          <ProtectedRoute requiredRole="Staff">
            <MyAttendanceSalary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-salary"
        element={
          <ProtectedRoute requiredRole="Staff">
            <MySalary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-salary-details/:month?/:year?"
        element={
          <ProtectedRoute requiredRole="Staff">
            <MySalaryDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-tasks"
        element={
          <ProtectedRoute requiredRole="Staff">
            <MyTasks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance-calendar/:month/:year"
        element={
          <ProtectedRoute requiredRole="Staff">
            <MonthlyAttendanceCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      {/* Expenses routes */}
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <Expenses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses/new"
        element={
          <ProtectedRoute requiredRole="Admin">
            <AddExpense />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses/:id"
        element={
          <ProtectedRoute>
            <ExpenseDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses/:expenseId/payments"
        element={
          <ProtectedRoute>
            <ExpensePaymentHistory />
          </ProtectedRoute>
        }
      />

      {/* Boutique Schedule routes */}
      <Route
        path="/boutique-schedule"
        element={
          <ProtectedRoute>
            <BoutiqueSchedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/boutique-schedule/:year/:month"
        element={
          <ProtectedRoute>
            <ScheduleCalendar />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* Catch-all for invalid routes - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}

export default App;
